import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { crmIntegrationService, brandService } from '../../services/api';
import { Spinner } from '../../components/Spinner';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  BeakerIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

// ── Helpers ───────────────────────────────────────────────────────────────────

const errMsg = (e, fallback) => e?.response?.data?.message || fallback;
const numList = (text) => String(text || '').split(/[\s,]+/).map(Number).filter(n => Number.isFinite(n) && n > 0);
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const pretty = (text) => {
  if (!text) return '';
  try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
};

const STATUS_STYLE = {
  sent:    'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed:  'bg-red-100 text-red-700',
};
const OUTCOME_STYLE = {
  sent: 'text-green-700', duplicate: 'text-green-700', retry: 'text-amber-700', failed: 'text-red-700',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'dealer', label: 'Dealer' },
  { value: 'sales_executive', label: 'Sales executive' },
];

const EMPTY = {
  name: '',
  accountUserId: '',
  eligibleRoles: ['dealer', 'sales_executive'],
  mode: 'staging',
  stagingUrl: '',
  productionUrl: '',
  method: 'POST',
  timeoutMs: 15000,
  headers: [],
  bodyTemplate: '{\n  \n}',
  routes: [],
  response: {
    successStatuses: '200, 201',
    externalIdPath: '',
    duplicateRules: [],
    retryStatuses: '429, 500, 502, 503, 504',
    retryDelaysSec: '30, 120, 600',
  },
};

/** API shape → form state (lists as text, route vars as rows). */
function toForm(doc) {
  if (!doc) return { ...EMPTY };
  return {
    name: doc.name || '',
    accountUserId: doc.accountUserId?._id || doc.accountUserId || '',
    eligibleRoles: doc.eligibleRoles?.length ? doc.eligibleRoles : EMPTY.eligibleRoles,
    mode: doc.mode || 'staging',
    stagingUrl: doc.stagingUrl || '',
    productionUrl: doc.productionUrl || '',
    method: doc.method || 'POST',
    timeoutMs: doc.timeoutMs || 15000,
    headers: (doc.headers || []).map(h => ({ name: h.name, value: h.secret ? '' : (h.value || ''), secret: !!h.secret, masked: h.masked || '', hasValue: !!h.hasValue })),
    bodyTemplate: (() => { try { return JSON.stringify(JSON.parse(doc.bodyTemplate || '{}'), null, 2); } catch { return doc.bodyTemplate || ''; } })(),
    // `saved` marks a destination that came from this integration's stored
    // record — its key is locked in the editor. New rows don't have it.
    routes: (doc.routes || []).map(r => ({ key: r.key, label: r.label || '', brands: r.brands || [], dealerIds: (r.dealerIds || []).map(String), vars: Object.entries(r.vars || {}).map(([k, v]) => ({ k, v })), saved: true })),
    response: {
      successStatuses: (doc.response?.successStatuses || [200, 201]).join(', '),
      externalIdPath: doc.response?.externalIdPath || '',
      duplicateRules: (doc.response?.duplicateRules || []).map(d => ({ status: d.status ? String(d.status) : '', bodyContains: d.bodyContains || '' })),
      retryStatuses: (doc.response?.retryStatuses || []).join(', '),
      retryDelaysSec: (doc.response?.retryDelaysSec || []).join(', '),
    },
  };
}

/** Form state → API body. */
function toBody(form) {
  return {
    name: form.name,
    accountUserId: form.accountUserId,
    eligibleRoles: form.eligibleRoles,
    mode: form.mode,
    stagingUrl: form.stagingUrl,
    productionUrl: form.productionUrl,
    method: form.method,
    timeoutMs: Number(form.timeoutMs) || 15000,
    headers: form.headers.map(h => ({ name: h.name, value: h.value, secret: h.secret })),
    bodyTemplate: form.bodyTemplate,
    routes: form.routes.map(r => ({
      key: r.key,
      label: r.label,
      brands: r.brands,
      dealerIds: r.dealerIds || [],
      vars: Object.fromEntries(r.vars.filter(x => x.k.trim()).map(x => [x.k.trim(), x.v])),
    })),
    response: {
      successStatuses: numList(form.response.successStatuses),
      externalIdPath: form.response.externalIdPath,
      duplicateRules: form.response.duplicateRules.map(d => ({ status: Number(d.status) || undefined, bodyContains: d.bodyContains })),
      retryStatuses: numList(form.response.retryStatuses),
      retryDelaysSec: numList(form.response.retryDelaysSec),
    },
  };
}

function Section({ title, hint, children }) {
  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

// `boxCls` has no width, for inputs sized inside a row; `inputCls` fills its cell.
const boxCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const inputCls = `w-full ${boxCls}`;

// ── Editor ────────────────────────────────────────────────────────────────────

function IntegrationEditor({ integration, others = [], meta, brands, onClose, onSaved }) {
  const [form, setForm] = useState(() => toForm(integration));
  const [showVars, setShowVars] = useState(false);
  const isNew = !integration;
  // A saved destination's key is what its sent leads are recorded under;
  // renaming it would make every recent lead look unsent and go again.
  const savedKeys = useMemo(() => new Set((integration?.routes || []).map(r => r.key)), [integration]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const setResponse = (patch) => setForm(f => ({ ...f, response: { ...f.response, ...patch } }));
  const setHeader = (i, patch) => set({ headers: form.headers.map((h, j) => (j === i ? { ...h, ...patch } : h)) });
  const setRoute = (i, patch) => set({ routes: form.routes.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const templateError = useMemo(() => {
    try {
      const v = JSON.parse(form.bodyTemplate || '{}');
      return v && typeof v === 'object' && !Array.isArray(v) ? '' : 'Must be a JSON object';
    } catch (e) { return e.message; }
  }, [form.bodyTemplate]);

  const save = useMutation(
    () => (isNew ? crmIntegrationService.create(toBody(form)) : crmIntegrationService.update(integration._id, toBody(form))),
    {
      onSuccess: () => { toast.success(isNew ? 'Integration added (switched off)' : 'Saved'); onSaved(); },
      onError: (e) => toast.error(errMsg(e, 'Could not save')),
    },
  );

  // Start from another integration's settings — the usual case when a second
  // dealer uses the same CRM. Whose leads, the name and the mode stay as they
  // are; secret values never leave the server, so they have to be entered.
  // Dealers on destinations belong to the other account, so they are cleared.
  const copyFrom = (id) => {
    const source = others.find(o => String(o._id) === String(id));
    if (!source) return;
    const from = toForm(source);
    setForm(f => ({
      ...f,
      eligibleRoles: from.eligibleRoles,
      stagingUrl: from.stagingUrl,
      productionUrl: from.productionUrl,
      method: from.method,
      timeoutMs: from.timeoutMs,
      headers: from.headers.map(h => ({ name: h.name, secret: h.secret, value: h.secret ? '' : h.value })),
      bodyTemplate: from.bodyTemplate,
      routes: from.routes.map(r => ({ ...r, dealerIds: [], saved: false })),
      response: from.response,
    }));
    const secrets = from.headers.filter(h => h.secret).map(h => h.name);
    toast.success(`Copied from ${source.name}.${secrets.length ? ` Enter ${secrets.join(', ')} — secrets are not copied.` : ''} Check the destinations.`, { duration: 6000 });
  };

  const accountOptions = (meta?.accounts || []).map(a => ({ value: a._id, label: `${a.name} (${a.role})` }));
  // Dealers a destination can be tied to: the chosen account's own dealers.
  const dealersUnder = (meta?.accounts || []).filter(a => a.role === 'dealer'
    && (String(a._id) === String(form.accountUserId) || (a.path || []).includes(String(form.accountUserId))));
  const dealerName = (id) => (meta?.accounts || []).find(a => String(a._id) === String(id))?.name || 'Unknown dealer';
  // A secret header with nothing typed and nothing stored would send every lead without its key.
  const missingSecrets = form.headers.filter(h => h.name.trim() && h.secret && !h.value && !h.hasValue).map(h => h.name.trim());
  // Each destination needs its own key (the server refuses a repeat)
  const keyCounts = form.routes.reduce((m, r) => {
    const k = (r.key || '').trim();
    if (k) m.set(k, (m.get(k) || 0) + 1);
    return m;
  }, new Map());
  const isDuplicateKey = (r) => (keyCounts.get((r.key || '').trim()) || 0) > 1;
  const hasDuplicateKeys = [...keyCounts.values()].some(n => n > 1);
  // Only a destination saved on this integration keeps its key
  const isKeyLocked = (r) => r.saved && savedKeys.has(r.key);
  const nextRouteKey = () => {
    let n = form.routes.length + 1;
    while (keyCounts.has(`route${n}`)) n++;
    return `route${n}`;
  };

  const canSave = form.name.trim() && form.accountUserId && !templateError && form.eligibleRoles.length && !missingSecrets.length && !hasDuplicateKeys;

  return (
    // Header and footer stay put; only the form between them scrolls, so
    // nothing can scroll out above the header.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-full flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex-none flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">{isNew ? 'Add CRM integration' : `Edit ${integration.name}`}</h3>
          <div className="flex items-center gap-2">
            {others.length > 0 && (
              <select
                value=""
                onChange={e => e.target.value && copyFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Copy settings from…</option>
                {others.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
              </select>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-7">
          <Section title="Whose leads" hint="Leads go to this CRM once someone in this account's team, in one of the chosen roles, holds them.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name *">
                <input className={inputCls} value={form.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. the dealer's CRM name" />
              </Field>
              <Field label="Account (this account and everyone under it) *">
                <SearchableSelect
                  value={form.accountUserId}
                  onChange={(v) => set({ accountUserId: v })}
                  options={accountOptions}
                  allLabel="Choose an account…"
                  placeholder="Search…"
                  buttonClassName={`${inputCls} text-left`}
                />
              </Field>
            </div>
            <Field label="Send when the lead is held by">
              <div className="flex flex-wrap gap-4">
                {ROLE_OPTIONS.map(r => (
                  <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      checked={form.eligibleRoles.includes(r.value)}
                      onChange={e => set({ eligibleRoles: e.target.checked ? [...form.eligibleRoles, r.value] : form.eligibleRoles.filter(x => x !== r.value) })}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </Field>
          </Section>

          <Section title="Connection">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Send to">
                <select className={inputCls} value={form.mode} onChange={e => set({ mode: e.target.value })}>
                  <option value="staging">Staging (test)</option>
                  <option value="production">Production (live)</option>
                </select>
              </Field>
              <Field label="Method">
                <select className={inputCls} value={form.method} onChange={e => set({ method: e.target.value })}>
                  <option>POST</option>
                  <option>PUT</option>
                </select>
              </Field>
              <Field label="Timeout (ms)">
                <input type="number" className={inputCls} value={form.timeoutMs} onChange={e => set({ timeoutMs: e.target.value })} />
              </Field>
            </div>
            <Field label="Staging URL" hint="Placeholders work here too, e.g. ?branchId={{route.branchId}}">
              <input className={`${inputCls} font-mono text-xs`} value={form.stagingUrl} onChange={e => set({ stagingUrl: e.target.value })} placeholder="https://dev-api.example.com/leads" />
            </Field>
            <Field label="Production URL">
              <input className={`${inputCls} font-mono text-xs`} value={form.productionUrl} onChange={e => set({ productionUrl: e.target.value })} placeholder="https://api.example.com/leads" />
            </Field>

            <Field label="Headers" hint="Mark API keys as secret: they are stored encrypted and never shown again. Leave a secret blank to keep the saved one.">
              <div className="space-y-2">
                {form.headers.map((h, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input className={`${boxCls} font-mono text-xs w-48 flex-none`} value={h.name} onChange={e => setHeader(i, { name: e.target.value })} placeholder="x-api-key" />
                    <input
                      className={`${boxCls} font-mono text-xs flex-1 min-w-0`}
                      type={h.secret ? 'password' : 'text'}
                      value={h.value}
                      onChange={e => setHeader(i, { value: e.target.value })}
                      placeholder={h.secret && h.hasValue ? `Saved: ${h.masked} — leave blank to keep` : 'value'}
                      autoComplete="new-password"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <input type="checkbox" checked={h.secret} onChange={e => setHeader(i, { secret: e.target.checked })} /> Secret
                    </label>
                    <button type="button" onClick={() => set({ headers: form.headers.filter((_, j) => j !== i) })} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                ))}
                {missingSecrets.length > 0 && (
                  <p className="text-xs text-red-600">Enter the value for {missingSecrets.join(', ')} — secrets are never copied or shown.</p>
                )}
                <button type="button" onClick={() => set({ headers: [...form.headers, { name: '', value: '', secret: false }] })} className="text-xs text-primary-600 hover:underline">+ Add header</button>
              </div>
            </Field>
          </Section>

          <Section title="Destinations" hint="A destination can be limited to brands and to dealers (none chosen = any). One tied to the lead's own dealer wins over one open to all dealers; otherwise the first match. Its variables fill {{route.<name>}} in the URL and body. A lead no destination takes is not sent.">
            <div className="space-y-3">
              {form.routes.map((r, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input className={inputCls} value={r.label} onChange={e => setRoute(i, { label: e.target.value })} placeholder="Label, e.g. Skoda Patparganj" />
                    <div>
                      <input
                        className={`${inputCls} font-mono text-xs disabled:bg-gray-50 disabled:text-gray-500 ${isDuplicateKey(r) && !isKeyLocked(r) ? 'border-red-400 focus:ring-red-400' : ''}`}
                        value={r.key}
                        onChange={e => setRoute(i, { key: e.target.value })}
                        placeholder="key, e.g. skoda"
                        disabled={isKeyLocked(r)}
                        title={isKeyLocked(r) ? 'A saved destination keeps its key, so leads already sent are not sent again' : 'Short id for this destination; cannot be changed once saved'}
                      />
                      {isDuplicateKey(r) && !isKeyLocked(r) && (
                        <p className="text-xs text-red-600 mt-1">
                          Another destination already uses this key — give this one its own, e.g. {`${(r.key || '').trim()}-${(r.label || 'two').trim().split(/\s+/).pop().toLowerCase()}`}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <select
                        className={inputCls}
                        value=""
                        onChange={e => e.target.value && !r.brands.includes(e.target.value) && setRoute(i, { brands: [...r.brands, e.target.value] })}
                      >
                        <option value="">+ Brand…</option>
                        {brands.map(b => <option key={b._id} value={b.name}>{b.name}</option>)}
                      </select>
                      <button type="button" onClick={() => set({ routes: form.routes.filter((_, j) => j !== i) })} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.brands.length ? r.brands.map(b => (
                      <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-xs">
                        {b}
                        <button type="button" onClick={() => setRoute(i, { brands: r.brands.filter(x => x !== b) })}><XMarkIcon className="h-3 w-3" /></button>
                      </span>
                    )) : <span className="text-xs text-gray-400">Any brand</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value=""
                      disabled={!form.accountUserId}
                      onChange={e => e.target.value && !(r.dealerIds || []).includes(e.target.value) && setRoute(i, { dealerIds: [...(r.dealerIds || []), e.target.value] })}
                    >
                      <option value="">{form.accountUserId ? '+ Dealer…' : 'Choose the account first'}</option>
                      {dealersUnder.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                    </select>
                    {(r.dealerIds || []).length ? r.dealerIds.map(id => (
                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs">
                        {dealerName(id)}
                        <button type="button" onClick={() => setRoute(i, { dealerIds: r.dealerIds.filter(x => x !== id) })}><XMarkIcon className="h-3 w-3" /></button>
                      </span>
                    )) : <span className="text-xs text-gray-400">Any dealer</span>}
                  </div>
                  {r.vars.map((v, k) => (
                    <div key={k} className="flex gap-2">
                      <input className={`${boxCls} font-mono text-xs w-40 flex-none`} value={v.k} onChange={e => setRoute(i, { vars: r.vars.map((x, m) => (m === k ? { ...x, k: e.target.value } : x)) })} placeholder="branchId" />
                      <input className={`${boxCls} font-mono text-xs flex-1 min-w-0`} value={v.v} onChange={e => setRoute(i, { vars: r.vars.map((x, m) => (m === k ? { ...x, v: e.target.value } : x)) })} placeholder="value" />
                      <button type="button" onClick={() => setRoute(i, { vars: r.vars.filter((_, m) => m !== k) })} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setRoute(i, { vars: [...r.vars, { k: '', v: '' }] })} className="text-xs text-primary-600 hover:underline">+ Add variable</button>
                </div>
              ))}
              <button type="button" onClick={() => set({ routes: [...form.routes, { key: nextRouteKey(), label: '', brands: [], dealerIds: [], vars: [] }] })} className="text-xs text-primary-600 hover:underline">+ Add destination</button>
            </div>
          </Section>

          <Section title="Request body" hint="JSON. A value that is only a placeholder is left out when the lead has nothing for it.">
            <textarea
              className={`${inputCls} font-mono text-xs h-56 ${templateError ? 'border-red-400' : ''}`}
              value={form.bodyTemplate}
              onChange={e => set({ bodyTemplate: e.target.value })}
              spellCheck={false}
            />
            {templateError && <p className="text-xs text-red-600">Not valid JSON: {templateError}</p>}
            <button type="button" onClick={() => setShowVars(s => !s)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
              {showVars ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />} Placeholders you can use
            </button>
            {showVars && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 bg-gray-50 rounded-lg p-3">
                {(meta?.variables || []).map(v => (
                  <div key={v.name} className="text-xs">
                    <code className="font-mono text-gray-800">{`{{${v.name}}}`}</code>
                    <span className="text-gray-500 ml-1">— {v.hint}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Reading the answer">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Success status codes" hint="Comma separated, e.g. 201">
                <input className={inputCls} value={form.response.successStatuses} onChange={e => setResponse({ successStatuses: e.target.value })} />
              </Field>
              <Field label="Where the CRM's lead ID is in the answer" hint="Dotted path, e.g. data._id">
                <input className={`${inputCls} font-mono text-xs`} value={form.response.externalIdPath} onChange={e => setResponse({ externalIdPath: e.target.value })} />
              </Field>
              <Field label="Retry on these status codes" hint="Timeouts and network errors are always retried">
                <input className={inputCls} value={form.response.retryStatuses} onChange={e => setResponse({ retryStatuses: e.target.value })} />
              </Field>
              <Field label="Wait before each retry (seconds)" hint="e.g. 30, 120, 600 = three retries">
                <input className={inputCls} value={form.response.retryDelaysSec} onChange={e => setResponse({ retryDelaysSec: e.target.value })} />
              </Field>
            </div>
            <Field label="Already in the CRM (counts as delivered, never resent)" hint="Match the status and text in the answer, so a setup error with the same status is not mistaken for a duplicate.">
              <div className="space-y-2">
                {form.response.duplicateRules.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <input className={`${boxCls} w-24 flex-none`} value={d.status} onChange={e => setResponse({ duplicateRules: form.response.duplicateRules.map((x, j) => (j === i ? { ...x, status: e.target.value } : x)) })} placeholder="404" />
                    <input className={`${boxCls} flex-1 min-w-0`} value={d.bodyContains} onChange={e => setResponse({ duplicateRules: form.response.duplicateRules.map((x, j) => (j === i ? { ...x, bodyContains: e.target.value } : x)) })} placeholder="text in the answer, e.g. already exists" />
                    <button type="button" onClick={() => setResponse({ duplicateRules: form.response.duplicateRules.filter((_, j) => j !== i) })} className="p-1.5 text-gray-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setResponse({ duplicateRules: [...form.response.duplicateRules, { status: '', bodyContains: '' }] })} className="text-xs text-primary-600 hover:underline">+ Add rule</button>
              </div>
            </Field>
          </Section>
        </div>

        <div className="flex-none px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">{isNew ? 'New integrations start switched off. Test before switching on.' : 'Changes apply to the next lead sent.'}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="button" disabled={!canSave || save.isLoading} onClick={() => save.mutate()} className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {save.isLoading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Test ──────────────────────────────────────────────────────────────────────

function TestPanel({ integration, onClose }) {
  const [mode, setMode] = useState('staging');
  const [routeKey, setRouteKey] = useState(integration.routes?.[0]?.key || '');
  const [leadId, setLeadId] = useState('');
  const [result, setResult] = useState(null);

  const run = useMutation(
    (dryRun) => crmIntegrationService.test(integration._id, { mode, routeKey: routeKey || undefined, leadId: leadId.trim() || undefined, dryRun }),
    {
      onSuccess: (res) => setResult(res.data.data),
      onError: (e) => { setResult(null); toast.error(errMsg(e, 'Test failed')); },
    },
  );

  const send = () => {
    if (mode === 'production' && !window.confirm('This sends a real lead to the live CRM. Continue?')) return;
    run.mutate(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Test {integration.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Sends one request. Nothing is recorded against a lead.</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Send to">
              <select className={inputCls} value={mode} onChange={e => setMode(e.target.value)}>
                <option value="staging">Staging (test)</option>
                <option value="production">Production (live)</option>
              </select>
            </Field>
            <Field label="Destination">
              <select className={inputCls} value={routeKey} onChange={e => setRouteKey(e.target.value)}>
                {(integration.routes || []).length === 0 && <option value="">Default</option>}
                {(integration.routes || []).map(r => <option key={r.key} value={r.key}>{r.label || r.key}</option>)}
              </select>
            </Field>
            <Field label="Real lead ID (optional)" hint="Blank = a sample lead, 9999999999">
              <input className={`${inputCls} font-mono text-xs`} value={leadId} onChange={e => setLeadId(e.target.value)} placeholder="Lead ID" />
            </Field>
          </div>
          <div className="flex gap-2">
            <button type="button" disabled={run.isLoading} onClick={() => run.mutate(true)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Preview request</button>
            <button type="button" disabled={run.isLoading} onClick={send} className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {run.isLoading ? 'Sending…' : 'Send test'}
            </button>
          </div>

          {result && (
            <div className="space-y-3">
              {result.outcome && (
                <div className={`text-sm font-medium ${OUTCOME_STYLE[result.outcome] || 'text-gray-700'}`}>
                  Result: {result.outcome === 'duplicate' ? 'already in the CRM (counts as delivered)' : result.outcome}
                  {result.externalId && <span className="font-normal text-gray-600"> · CRM ID {result.externalId}</span>}
                  {result.reason && <span className="font-normal text-gray-600"> · {result.reason}</span>}
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Request</p>
                <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
{`${result.request.method} ${result.request.url}
${Object.entries(result.request.headers || {}).map(([k, v]) => `${k}: ${v}`).join('\n')}

${pretty(result.request.body)}`}
                </pre>
              </div>
              {result.response && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Response {result.response.status ? `· HTTP ${result.response.status}` : ''} · {result.response.durationMs} ms
                  </p>
                  <pre className="bg-gray-50 border border-gray-200 text-gray-800 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {result.response.error || pretty(result.response.body) || '(empty)'}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Integrations list ─────────────────────────────────────────────────────────

function IntegrationCard({ item, onEdit, onTest, onLog }) {
  const queryClient = useQueryClient();
  const toggle = useMutation(
    () => crmIntegrationService.update(item._id, { isActive: !item.isActive }),
    {
      onSuccess: () => { toast.success(item.isActive ? 'Switched off' : 'Switched on'); queryClient.invalidateQueries('crmIntegrations'); },
      onError: (e) => toast.error(errMsg(e, 'Could not change it')),
    },
  );
  const remove = useMutation(() => crmIntegrationService.remove(item._id), {
    onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries('crmIntegrations'); },
    onError: (e) => toast.error(errMsg(e, 'Could not delete')),
  });

  const confirmToggle = () => {
    const msg = item.isActive
      ? `Stop sending leads to ${item.name}? Leads waiting to go will wait until it is switched on again.`
      : `Start sending leads to ${item.name} (${item.mode})? Leads handed to the team from now on will be sent.`;
    if (window.confirm(msg)) toggle.mutate();
  };

  const account = item.accountUserId;
  const c = item.counts || {};
  return (
    <div className="card">
      <div className="card-body space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{item.name}</h3>
              <span className={`badge ${item.mode === 'production' ? 'badge-primary' : 'badge-warning'}`}>{item.mode}</span>
              <span className={`badge ${item.isActive ? 'badge-success' : 'badge-secondary'}`}>{item.isActive ? 'On' : 'Off'}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leads of {account ? `${account.firstName} ${account.lastName}`.trim() : '—'} and their team
              {item.routes?.length ? ` · ${item.routes.map(r => r.label || r.key).join(', ')}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onTest} className="p-1.5 text-gray-400 hover:text-primary-600" title="Test"><BeakerIcon className="h-4 w-4" /></button>
            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-primary-600" title="Edit"><PencilSquareIcon className="h-4 w-4" /></button>
            {!item.isActive && (
              <button onClick={() => window.confirm(`Delete ${item.name}?`) && remove.mutate()} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><TrashIcon className="h-4 w-4" /></button>
            )}
            <button
              onClick={confirmToggle}
              disabled={toggle.isLoading}
              className={`ml-2 px-3 py-1.5 text-xs rounded-lg ${item.isActive ? 'border border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
            >
              {item.isActive ? 'Switch off' : 'Switch on'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <button onClick={() => onLog('sent')} className="text-left rounded-lg bg-green-50 px-3 py-2 hover:bg-green-100">
            <div className="text-xs text-green-700">Sent</div>
            <div className="font-semibold text-green-800">{c.sent || 0} <span className="text-xs font-normal">({c.sentLast24h || 0} in 24 h)</span></div>
          </button>
          <button onClick={() => onLog('pending')} className="text-left rounded-lg bg-amber-50 px-3 py-2 hover:bg-amber-100">
            <div className="text-xs text-amber-700">Waiting</div>
            <div className="font-semibold text-amber-800">{c.pending || 0}</div>
          </button>
          <button onClick={() => onLog('failed')} className="text-left rounded-lg bg-red-50 px-3 py-2 hover:bg-red-100">
            <div className="text-xs text-red-700">Failed</div>
            <div className="font-semibold text-red-800">{c.failed || 0} <span className="text-xs font-normal">({c.failedLast24h || 0} in 24 h)</span></div>
          </button>
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <div className="text-xs text-gray-500">Last success</div>
            <div className="text-xs text-gray-800 mt-0.5">{fmtTime(item.stats?.lastSuccessAt)}</div>
          </div>
        </div>
        {item.stats?.consecutiveFailures > 0 && item.stats?.lastError && (
          <p className="text-xs text-red-600">Last error ({fmtTime(item.stats.lastFailureAt)}): {item.stats.lastError}</p>
        )}
      </div>
    </div>
  );
}

// ── Sync log ──────────────────────────────────────────────────────────────────

function DeliveryCalls({ deliveryId }) {
  const { data, isLoading } = useQuery(['crmLogs', deliveryId], () => crmIntegrationService.logs({ deliveryId, limit: 20 }));
  const logs = data?.data?.data || [];
  if (isLoading) return <div className="py-3"><Spinner size="sm" /></div>;
  if (!logs.length) return <p className="text-xs text-gray-400 py-2">No calls made yet.</p>;
  return (
    <div className="space-y-3 py-2">
      {logs.map(l => (
        <div key={l._id} className="text-xs">
          <div className="flex gap-3 text-gray-600">
            <span>{fmtTime(l.createdAt)}</span>
            <span>Attempt {l.attempt}</span>
            <span className={OUTCOME_STYLE[l.outcome]}>{l.outcome}</span>
            {l.httpStatus && <span>HTTP {l.httpStatus}</span>}
            {l.durationMs !== undefined && <span>{l.durationMs} ms</span>}
            <span className="uppercase">{l.mode}</span>
          </div>
          {l.error && <p className="text-red-600 mt-1">{l.error}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            <pre className="bg-gray-900 text-green-300 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{`${l.method || ''} ${l.url || ''}\n\n${pretty(l.requestBody)}`}</pre>
            <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{pretty(l.responseBody) || '(no response)'}</pre>
          </div>
        </div>
      ))}
    </div>
  );
}

function SyncLog({ integrations, initialFilter }) {
  const queryClient = useQueryClient();
  const [integrationId, setIntegrationId] = useState(initialFilter?.integrationId || '');
  const [status, setStatus] = useState(initialFilter?.status || '');
  const [phone, setPhone] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (initialFilter) {
      setIntegrationId(initialFilter.integrationId || '');
      setStatus(initialFilter.status || '');
    }
  }, [initialFilter]);

  const params = { integrationId: integrationId || undefined, status: status || undefined, phone: phoneQuery || undefined, limit: 100 };
  const { data, isLoading, refetch, isFetching } = useQuery(['crmDeliveries', params], () => crmIntegrationService.deliveries(params), { keepPreviousData: true });
  const rows = data?.data?.data || [];

  const resend = useMutation((id) => crmIntegrationService.resend(id), {
    onSuccess: () => { toast.success('Queued to send again'); setTimeout(() => queryClient.invalidateQueries('crmDeliveries'), 1500); },
    onError: (e) => toast.error(errMsg(e, 'Could not resend')),
  });

  return (
    <div className="card">
      <div className="card-body space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <select className={`${boxCls} w-48`} value={integrationId} onChange={e => setIntegrationId(e.target.value)}>
            <option value="">All integrations</option>
            {integrations.map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
          </select>
          <select className={`${boxCls} w-36`} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="sent">Sent</option>
            <option value="pending">Waiting</option>
            <option value="failed">Failed</option>
          </select>
          <form onSubmit={e => { e.preventDefault(); setPhoneQuery(phone.trim()); }} className="flex items-center gap-1">
            <input className={`${boxCls} w-44`} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Customer phone" />
            <button type="submit" className="p-2 text-gray-500 hover:text-primary-600"><MagnifyingGlassIcon className="h-4 w-4" /></button>
          </form>
          <button onClick={() => refetch()} className="ml-auto p-2 text-gray-500 hover:text-primary-600" title="Refresh">
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {isLoading ? <Spinner /> : !rows.length ? (
          <p className="text-sm text-gray-500 py-6 text-center">Nothing here yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-3 w-6" />
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Integration</th>
                  <th className="py-2 pr-3">Destination</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">CRM ID</th>
                  <th className="py-2 pr-3">Updated</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <React.Fragment key={r._id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(open === r._id ? null : r._id)}>
                      <td className="py-2 pr-3 text-gray-400">{open === r._id ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}</td>
                      <td className="py-2 pr-3">
                        <div className="text-gray-900">{r.leadId?.customerInfo?.name || '(deleted lead)'}</div>
                        <div className="text-xs text-gray-500">{r.leadId?.customerInfo?.phone} {r.leadId?.leadInfo?.brand ? `· ${r.leadId.leadInfo.brand}` : ''}</div>
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{r.integrationId?.name}</td>
                      <td className="py-2 pr-3 text-gray-700">{r.routeLabel}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[r.display]}`}>
                          {r.status === 'duplicate' ? 'Already in CRM' : r.display === 'pending' ? 'Waiting' : r.display === 'sent' ? 'Sent' : 'Failed'}
                        </span>
                        {r.display !== 'sent' && r.lastError && <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={r.lastError}>{r.lastError}</div>}
                        {r.display === 'pending' && r.attempts > 0 && <div className="text-xs text-gray-500 mt-1">Retry at {fmtTime(r.nextAttemptAt)}</div>}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs text-gray-600">{r.externalId || '—'}</td>
                      <td className="py-2 pr-3 text-xs text-gray-500">{fmtTime(r.updatedAt)}</td>
                      <td className="py-2 pr-3">
                        {(r.status === 'failed' || r.status === 'pending') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); resend.mutate(r._id); }}
                            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"
                          >
                            Resend
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === r._id && (
                      <tr><td colSpan={8} className="px-3 bg-gray-50/60"><DeliveryCalls deliveryId={r._id} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const CrmIntegrations = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === 'super_admin';
  const [tab, setTab] = useState('integrations');
  const [editing, setEditing] = useState(undefined);   // undefined = closed, null = new
  const [testing, setTesting] = useState(null);
  const [logFilter, setLogFilter] = useState(null);

  const { data, isLoading } = useQuery('crmIntegrations', crmIntegrationService.list, { enabled: isSuperAdmin, refetchInterval: 30000 });
  const { data: metaData } = useQuery('crmIntegrationsMeta', crmIntegrationService.meta, { enabled: isSuperAdmin, staleTime: 5 * 60 * 1000 });
  const { data: brandData } = useQuery('brands', brandService.getBrands, { enabled: isSuperAdmin, staleTime: 5 * 60 * 1000 });

  const items = data?.data?.data || [];
  const meta = metaData?.data?.data;
  const brands = brandData?.data?.data?.brands || [];

  if (!isSuperAdmin) {
    return <div className="p-6 text-sm text-gray-500">Only the super admin can manage CRM integrations.</div>;
  }

  const openLog = (integrationId, status) => { setLogFilter({ integrationId, status, at: Date.now() }); setTab('log'); };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRM Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Send leads into dealers' own CRMs as soon as their team gets them.</p>
        </div>
        {tab === 'integrations' && (
          <button onClick={() => setEditing(null)} className="btn-primary flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" /> Add integration
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {[['integrations', 'Integrations'], ['log', 'Sync log']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'integrations' && (
        isLoading ? <Spinner /> : !items.length ? (
          <div className="card"><div className="card-body text-center py-10">
            <p className="text-sm text-gray-500">No CRM integrations yet.</p>
            <button onClick={() => setEditing(null)} className="mt-3 text-sm text-primary-600 hover:underline">Add the first one</button>
          </div></div>
        ) : (
          <div className="space-y-4">
            {items.map(item => (
              <IntegrationCard
                key={item._id}
                item={item}
                onEdit={() => setEditing(item)}
                onTest={() => setTesting(item)}
                onLog={(status) => openLog(item._id, status)}
              />
            ))}
          </div>
        )
      )}

      {tab === 'log' && <SyncLog integrations={items} initialFilter={logFilter} />}

      {editing !== undefined && (
        <IntegrationEditor
          integration={editing}
          others={items.filter(i => !editing || i._id !== editing._id)}
          meta={meta}
          brands={brands}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); queryClient.invalidateQueries('crmIntegrations'); }}
        />
      )}
      {testing && <TestPanel integration={testing} onClose={() => setTesting(null)} />}
    </div>
  );
};
